import AddIcon from "@mui/icons-material/Add"
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import EventOutlinedIcon from "@mui/icons-material/EventOutlined"
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined"
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"
import SearchIcon from "@mui/icons-material/Search"
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined"
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined"

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"
import * as hrContractService from "../../services/hrContractService"


// =========================================================
// HELPERS
// =========================================================

const todayIso = () =>
  new Date()
    .toISOString()
    .slice(0, 10)


const money = (
  value,
  currency = "USD"
) =>
  `${new Intl.NumberFormat(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(
    Number(
      value || 0
    )
  )} ${currency || "USD"}`


const formatDate = (
  value
) => {
  if (!value) {
    return "-"
  }

  const normalized =
    String(value)
      .slice(0, 10)

  const date = new Date(
    `${normalized}T00:00:00`
  )

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value
  }

  return new Intl.DateTimeFormat(
    "fr-FR"
  ).format(date)
}


const getErrorMessage = (
  err,
  fallback
) =>
  err?.response?.data
    ?.detail?.message ||
  err?.response?.data
    ?.detail ||
  fallback


const formatBytes = (
  value
) => {
  const bytes = Number(
    value || 0
  )

  if (!bytes) {
    return "-"
  }

  if (bytes < 1024) {
    return `${bytes} o`
  }

  if (bytes < 1024 ** 2) {
    return `${(
      bytes / 1024
    ).toFixed(1)} Ko`
  }

  return `${(
    bytes / (1024 ** 2)
  ).toFixed(1)} Mo`
}


const CONTRACT_TYPE_LABELS = {
  PERMANENT: "CDI",
  FIXED_TERM: "CDD",
  TEMPORARY: "Temporaire",
  INTERNSHIP: "Stage",
  CONSULTANT: "Consultant",
  DAILY: "Journalier",
  OTHER: "Autre",
}


const STATUS_CONFIG = {
  DRAFT: {
    label: "Brouillon",
    color: "default",
  },
  ACTIVE: {
    label: "Actif",
    color: "success",
  },
  EXPIRED: {
    label: "Expiré",
    color: "warning",
  },
  TERMINATED: {
    label: "Résilié",
    color: "error",
  },
  RENEWED: {
    label: "Renouvelé",
    color: "info",
  },
}


const emptyContractForm = {
  employee_id: "",
  contract_type: "FIXED_TERM",
  start_date: todayIso(),
  end_date: "",
  probation_end_date: "",
  salary: 0,
  currency: "USD",
  status: "ACTIVE",
  signed_at: "",
  notes: "",
}


const emptyRenewForm = {
  start_date: todayIso(),
  end_date: "",
  probation_end_date: "",
  salary: 0,
  currency: "USD",
  contract_type: "FIXED_TERM",
  signed_at: "",
  notes: "",
}


const emptyDocumentForm = {
  employee_id: "",
  contract_id: "",
  document_type: "CV",
  title: "",
  issue_date: "",
  expiry_date: "",
  notes: "",
}


// =========================================================
// KPI CARD
// =========================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        minHeight: 118,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            variant="h4"
            fontWeight={900}
            sx={{
              lineHeight: 1.15,
              mt: 0.25,
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

export default function ContractsDocumentsPage() {
  const fileInputRef = useRef(null)

  const [tab, setTab] = useState(0)

  const [contracts, setContracts] = useState([])
  const [documents, setDocuments] = useState([])

  const [formData, setFormData] = useState({
    employees: [],
    contract_types: [],
    statuses: [],
    currencies: [],
    document_types: [],
    upload: {
      max_file_size_mb: 10,
      allowed_extensions: [],
    },
  })

  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    draft: 0,
    expired: 0,
    terminated: 0,
    renewed: 0,
    expiring_30_days: 0,
    documents: 0,
  })

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [typeFilter, setTypeFilter] = useState("all")

  const [contractDialogOpen, setContractDialogOpen] = useState(false)
  const [editingContract, setEditingContract] = useState(null)
  const [contractForm, setContractForm] = useState(emptyContractForm)

  const [renewDialogOpen, setRenewDialogOpen] = useState(false)
  const [renewContractTarget, setRenewContractTarget] = useState(null)
  const [renewForm, setRenewForm] = useState(emptyRenewForm)

  const [terminationDialogOpen, setTerminationDialogOpen] = useState(false)
  const [terminationTarget, setTerminationTarget] = useState(null)
  const [terminationForm, setTerminationForm] = useState({
    termination_date: todayIso(),
    reason: "",
  })

  const [documentDialogOpen, setDocumentDialogOpen] = useState(false)
  const [documentForm, setDocumentForm] = useState(emptyDocumentForm)
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)

  const [deleteDocumentTarget, setDeleteDocumentTarget] = useState(null)

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // NOTIFICATION
  // =======================================================

  const notify = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  // =======================================================
  // LOADERS
  // =======================================================

  const loadContracts = useCallback(async () => {
    const result = await hrContractService.getHrContracts({
      search,
      status: statusFilter,
      contract_type: typeFilter,
    })

    setContracts(
      Array.isArray(result)
        ? result
        : result?.items || []
    )
  }, [
    search,
    statusFilter,
    typeFilter,
  ])


  const loadDocuments = useCallback(async () => {
    const result = await hrContractService.getHrDocuments({
      search,
    })

    setDocuments(
      Array.isArray(result)
        ? result
        : result?.items || []
    )
  }, [search])


  const loadFormData = useCallback(async () => {
    const result = await hrContractService.getHrContractFormData()

    setFormData({
      employees: result?.employees || [],
      contract_types: result?.contract_types || [],
      statuses: result?.statuses || [],
      currencies: result?.currencies || [],
      document_types: result?.document_types || [],
      upload: result?.upload || {
        max_file_size_mb: 10,
        allowed_extensions: [],
      },
    })
  }, [])


  const loadSummary = useCallback(async () => {
    const result = await hrContractService.getHrContractSummary()

    setSummary((current) => ({
      ...current,
      ...(result || {}),
    }))
  }, [])


  const loadData = useCallback(async () => {
    setLoading(true)
    setError("")

    try {
      await Promise.all([
        loadContracts(),
        loadDocuments(),
        loadFormData(),
        loadSummary(),
      ])
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "Impossible de charger le module contrats et documents."
        )
      )
    } finally {
      setLoading(false)
    }
  }, [
    loadContracts,
    loadDocuments,
    loadFormData,
    loadSummary,
  ])


  useEffect(() => {
    loadData()
  }, [loadData])


  // =======================================================
  // REFRESH
  // =======================================================

  const handleRefresh = async () => {
    setLoading(true)

    try {
      await hrContractService.refreshExpiredHrContracts()
      await loadData()

      notify("Contrats et documents actualisés.")
    } catch (err) {
      notify(
        getErrorMessage(
          err,
          "Impossible d'actualiser le module."
        ),
        "error"
      )
    } finally {
      setLoading(false)
    }
  }


  // =======================================================
  // CONTRACT CREATE / EDIT
  // =======================================================

  const openCreateContract = () => {
    setEditingContract(null)
    setContractForm({ ...emptyContractForm })
    setContractDialogOpen(true)
  }


  const openEditContract = (
    row
  ) => {
    setEditingContract(row)

    setContractForm({
      employee_id: row.employee_id || "",
      contract_type: row.contract_type || "FIXED_TERM",
      start_date: row.start_date
        ? String(row.start_date).slice(0, 10)
        : "",
      end_date: row.end_date
        ? String(row.end_date).slice(0, 10)
        : "",
      probation_end_date: row.probation_end_date
        ? String(row.probation_end_date).slice(0, 10)
        : "",
      salary: Number(row.salary || 0),
      currency: row.currency || "USD",
      status: row.status || "ACTIVE",
      signed_at: row.signed_at
        ? String(row.signed_at).slice(0, 10)
        : "",
      notes: row.notes || "",
    })

    setContractDialogOpen(true)
  }


  const handleEmployeeChange = (
    event
  ) => {
    const employeeId = Number(
      event.target.value
    )

    const employee = formData.employees.find(
      (item) => Number(item.id) === employeeId
    )

    setContractForm((current) => ({
      ...current,
      employee_id: employeeId,
      salary: Number(
        employee?.base_salary ??
          current.salary ??
          0
      ),
      currency:
        employee?.currency ||
        current.currency ||
        "USD",
    }))
  }


  const handleSaveContract = async () => {
    if (!contractForm.employee_id) {
      notify("Sélectionnez un employé.", "warning")
      return
    }

    if (!contractForm.contract_type) {
      notify("Sélectionnez un type de contrat.", "warning")
      return
    }

    if (!contractForm.start_date) {
      notify("La date de début est obligatoire.", "warning")
      return
    }

    const payload = {
      employee_id: Number(contractForm.employee_id),
      contract_type: contractForm.contract_type,
      start_date: contractForm.start_date,
      end_date: contractForm.end_date || null,
      probation_end_date:
        contractForm.probation_end_date || null,
      salary: Number(contractForm.salary || 0),
      currency: contractForm.currency,
      status: contractForm.status,
      signed_at: contractForm.signed_at || null,
      notes: contractForm.notes || null,
    }

    setSaving(true)

    try {
      const result = editingContract
        ? await hrContractService.updateHrContract(
            editingContract.id,
            payload
          )
        : await hrContractService.createHrContract(
            payload
          )

      setContractDialogOpen(false)
      await loadData()

      notify(
        result?.message ||
          (editingContract
            ? "Contrat modifié avec succès."
            : "Contrat créé avec succès.")
      )
    } catch (err) {
      notify(
        getErrorMessage(
          err,
          "Impossible d'enregistrer le contrat."
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // RENEWAL
  // =======================================================

  const openRenew = (
    row
  ) => {
    setRenewContractTarget(row)

    let nextStart = todayIso()

    if (row.end_date) {
      const end = new Date(
        `${String(row.end_date).slice(0, 10)}T00:00:00`
      )
      end.setDate(end.getDate() + 1)
      nextStart = end.toISOString().slice(0, 10)
    }

    setRenewForm({
      ...emptyRenewForm,
      start_date: nextStart,
      contract_type: row.contract_type || "FIXED_TERM",
      salary: Number(row.salary || 0),
      currency: row.currency || "USD",
    })

    setRenewDialogOpen(true)
  }


  const handleRenew = async () => {
    if (!renewContractTarget) {
      return
    }

    if (!renewForm.start_date) {
      notify(
        "La date de début du nouveau contrat est obligatoire.",
        "warning"
      )
      return
    }

    setSaving(true)

    try {
      const result = await hrContractService.renewHrContract(
        renewContractTarget.id,
        {
          start_date: renewForm.start_date,
          end_date: renewForm.end_date || null,
          probation_end_date:
            renewForm.probation_end_date || null,
          salary: Number(renewForm.salary || 0),
          currency: renewForm.currency,
          contract_type: renewForm.contract_type,
          signed_at: renewForm.signed_at || null,
          notes: renewForm.notes || null,
        }
      )

      setRenewDialogOpen(false)
      await loadData()

      notify(
        result?.message ||
          "Contrat renouvelé avec succès."
      )
    } catch (err) {
      notify(
        getErrorMessage(
          err,
          "Impossible de renouveler le contrat."
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // TERMINATION
  // =======================================================

  const openTermination = (
    row
  ) => {
    setTerminationTarget(row)
    setTerminationForm({
      termination_date: todayIso(),
      reason: "",
    })
    setTerminationDialogOpen(true)
  }


  const handleTerminate = async () => {
    if (!terminationTarget) {
      return
    }

    if (!terminationForm.reason.trim()) {
      notify(
        "Indiquez le motif de résiliation.",
        "warning"
      )
      return
    }

    setSaving(true)

    try {
      const result = await hrContractService.terminateHrContract(
        terminationTarget.id,
        {
          termination_date:
            terminationForm.termination_date,
          reason: terminationForm.reason,
        }
      )

      setTerminationDialogOpen(false)
      await loadData()

      notify(
        result?.message ||
          "Contrat résilié avec succès."
      )
    } catch (err) {
      notify(
        getErrorMessage(
          err,
          "Impossible de résilier le contrat."
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // DOCUMENT UPLOAD
  // =======================================================

  const openDocumentDialog = () => {
    setDocumentForm({ ...emptyDocumentForm })
    setSelectedFile(null)
    setUploadProgress(0)
    setDocumentDialogOpen(true)
  }


  const handleFileChange = (
    event
  ) => {
    const file = event.target.files?.[0] || null

    if (!file) {
      setSelectedFile(null)
      return
    }

    const maxMb = Number(
      formData.upload?.max_file_size_mb || 10
    )

    if (
      file.size >
      maxMb * 1024 * 1024
    ) {
      event.target.value = ""
      setSelectedFile(null)

      notify(
        `Le fichier dépasse la taille maximale de ${maxMb} Mo.`,
        "warning"
      )
      return
    }

    const extension = `.${
      file.name
        .split(".")
        .pop()
        ?.toLowerCase() || ""
    }`

    const allowed =
      formData.upload
        ?.allowed_extensions || []

    if (
      allowed.length &&
      !allowed.includes(extension)
    ) {
      event.target.value = ""
      setSelectedFile(null)

      notify(
        "Format non autorisé. Utilisez PDF, JPG, JPEG, PNG, DOC ou DOCX.",
        "warning"
      )
      return
    }

    setSelectedFile(file)

    setDocumentForm((current) => ({
      ...current,
      title:
        current.title ||
        file.name.replace(
          /\.[^/.]+$/,
          ""
        ),
    }))
  }


  const handleUploadDocument = async () => {
    if (!documentForm.employee_id) {
      notify("Sélectionnez un employé.", "warning")
      return
    }

    if (!documentForm.document_type) {
      notify("Sélectionnez le type du document.", "warning")
      return
    }

    if (!documentForm.title.trim()) {
      notify("Le titre du document est obligatoire.", "warning")
      return
    }

    if (!selectedFile) {
      notify("Choisissez un fichier à téléverser.", "warning")
      return
    }

    setSaving(true)
    setUploadProgress(0)

    try {
      const result = await hrContractService.uploadHrDocument(
        {
          employee_id:
            Number(documentForm.employee_id),
          contract_id:
            documentForm.contract_id
              ? Number(documentForm.contract_id)
              : null,
          document_type:
            documentForm.document_type,
          title:
            documentForm.title.trim(),
          issue_date:
            documentForm.issue_date || null,
          expiry_date:
            documentForm.expiry_date || null,
          notes:
            documentForm.notes || null,
        },
        selectedFile,
        (progressEvent) => {
          if (!progressEvent.total) {
            return
          }

          setUploadProgress(
            Math.round(
              (
                progressEvent.loaded * 100
              ) / progressEvent.total
            )
          )
        }
      )

      setDocumentDialogOpen(false)
      setDocumentForm({ ...emptyDocumentForm })
      setSelectedFile(null)
      setUploadProgress(0)

      await Promise.all([
        loadDocuments(),
        loadSummary(),
      ])

      notify(
        result?.message ||
          "Document téléversé avec succès."
      )
    } catch (err) {
      notify(
        getErrorMessage(
          err,
          "Impossible de téléverser le document."
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // DOCUMENT OPEN / DOWNLOAD / DELETE
  // =======================================================

  const openBlobInNewTab = (
    blob
  ) => {
    const url = URL.createObjectURL(blob)
    const newWindow = window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    )

    if (!newWindow) {
      URL.revokeObjectURL(url)
      notify(
        "Le navigateur a bloqué l'ouverture du document.",
        "warning"
      )
      return
    }

    setTimeout(() => {
      URL.revokeObjectURL(url)
    }, 60000)
  }


  const handleOpenDocument = async (
    row
  ) => {
    try {
      const response = await hrContractService.openHrDocumentFile(
        row.id
      )

      openBlobInNewTab(
        response.data
      )
    } catch (err) {
      notify(
        getErrorMessage(
          err,
          "Impossible d'ouvrir le document."
        ),
        "error"
      )
    }
  }


  const handleDownloadDocument = async (
    row
  ) => {
    try {
      const response = await hrContractService.downloadHrDocumentFile(
        row.id
      )

      const url = URL.createObjectURL(
        response.data
      )

      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download =
        row.file_name ||
        `document-${row.id}`

      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()

      URL.revokeObjectURL(url)
    } catch (err) {
      notify(
        getErrorMessage(
          err,
          "Impossible de télécharger le document."
        ),
        "error"
      )
    }
  }


  const handleDeleteDocument = async () => {
    if (!deleteDocumentTarget) {
      return
    }

    setSaving(true)

    try {
      const result = await hrContractService.deleteHrDocument(
        deleteDocumentTarget.id
      )

      setDeleteDocumentTarget(null)

      await Promise.all([
        loadDocuments(),
        loadSummary(),
      ])

      notify(
        result?.message ||
          "Document supprimé avec succès."
      )
    } catch (err) {
      notify(
        getErrorMessage(
          err,
          "Impossible de supprimer le document."
        ),
        "error"
      )
    } finally {
      setSaving(false)
    }
  }


  // =======================================================
  // DOCUMENT CONTRACTS
  // =======================================================

  const documentContracts = useMemo(
    () =>
      contracts.filter(
        (contract) =>
          !documentForm.employee_id ||
          Number(contract.employee_id) ===
            Number(documentForm.employee_id)
      ),
    [
      contracts,
      documentForm.employee_id,
    ]
  )


  // =======================================================
  // CONTRACT COLUMNS
  // =======================================================

  const contractColumns = [
    {
      field: "contract_code",
      headerName: "N° contrat",
      minWidth: 130,
    },
    {
      field: "employee_name",
      headerName: "Employé",
      minWidth: 190,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ py: 0.6, lineHeight: 1.2 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
          >
            {params.row.employee_name || "-"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
          >
            {params.row.employee_code || ""}
          </Typography>
        </Box>
      ),
    },
    {
      field: "contract_type",
      headerName: "Type",
      minWidth: 125,
      renderCell: (params) =>
        CONTRACT_TYPE_LABELS[
          String(params.value || "").toUpperCase()
        ] ||
        params.value ||
        "-",
    },
    {
      field: "start_date",
      headerName: "Début",
      minWidth: 115,
      valueFormatter: (value) =>
        formatDate(value),
    },
    {
      field: "end_date",
      headerName: "Fin",
      minWidth: 115,
      valueFormatter: (value) =>
        formatDate(value),
    },
    {
      field: "salary",
      headerName: "Salaire",
      minWidth: 140,
      align: "right",
      headerAlign: "right",
      renderCell: (params) =>
        money(
          params.value,
          params.row.currency
        ),
    },
    {
      field: "status",
      headerName: "Statut",
      minWidth: 120,
      renderCell: (params) => {
        const status = String(
          params.value || ""
        ).toUpperCase()

        const config =
          STATUS_CONFIG[status] || {
            label: params.value || "-",
            color: "default",
          }

        return (
          <Chip
            size="small"
            label={config.label}
            color={config.color}
          />
        )
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 170,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const row = params.row
        const active = row.status === "ACTIVE"

        return (
          <Stack
            direction="row"
            spacing={0.25}
          >
            <Tooltip title="Modifier">
              <IconButton
                size="small"
                onClick={() =>
                  openEditContract(row)
                }
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {active && (
              <Tooltip title="Renouveler">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() =>
                    openRenew(row)
                  }
                >
                  <AutorenewOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {active && (
              <Tooltip title="Résilier">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() =>
                    openTermination(row)
                  }
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )
      },
    },
  ]


  // =======================================================
  // DOCUMENT COLUMNS
  // =======================================================

  const documentColumns = [
    {
      field: "employee_name",
      headerName: "Employé",
      minWidth: 185,
      flex: 0.9,
      renderCell: (params) => (
        <Box sx={{ py: 0.6, lineHeight: 1.2 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
          >
            {params.row.employee_name || "-"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
          >
            {params.row.employee_code || ""}
          </Typography>
        </Box>
      ),
    },
    {
      field: "document_type",
      headerName: "Type",
      minWidth: 145,
    },
    {
      field: "title",
      headerName: "Document",
      minWidth: 220,
      flex: 1.1,
    },
    {
      field: "file_name",
      headerName: "Fichier",
      minWidth: 190,
      flex: 0.8,
      renderCell: (params) =>
        params.value || "-",
    },
    {
      field: "contract_code",
      headerName: "Contrat",
      minWidth: 125,
      renderCell: (params) =>
        params.value || "-",
    },
    {
      field: "file_size",
      headerName: "Taille",
      minWidth: 95,
      renderCell: (params) =>
        formatBytes(params.value),
    },
    {
      field: "expiry_date",
      headerName: "Expiration",
      minWidth: 115,
      valueFormatter: (value) =>
        formatDate(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 155,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack
          direction="row"
          spacing={0.25}
        >
          <Tooltip title="Consulter">
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                handleOpenDocument(params.row)
              }
            >
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Télécharger">
            <IconButton
              size="small"
              onClick={() =>
                handleDownloadDocument(params.row)
              }
            >
              <DownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Supprimer">
            <IconButton
              size="small"
              color="error"
              onClick={() =>
                setDeleteDocumentTarget(params.row)
              }
            >
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ]


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
            md: 3,
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                fontSize: {
                  xs: "1.7rem",
                  md: "2.125rem",
                },
              }}
            >
              Contrats & documents
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Contrats, renouvellements, échéances et documents administratifs du personnel.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Actualiser
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateContract}
            >
              Nouveau contrat
            </Button>

            <Button
              variant="outlined"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={openDocumentDialog}
            >
              Ajouter document
            </Button>
          </Stack>
        </Stack>

        <Alert
          severity="info"
          sx={{ mb: 2 }}
        >
          Les numéros de contrat sont générés automatiquement. Les documents RH sont maintenant téléversés directement depuis l'ordinateur ou le téléphone et peuvent ensuite être consultés ou téléchargés.
        </Alert>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            mb: 2,
          }}
        >
          <KpiCard
            title="Contrats"
            value={summary.total || 0}
            subtitle="Total enregistré"
            icon={<AssignmentOutlinedIcon />}
          />

          <KpiCard
            title="Actifs"
            value={summary.active || 0}
            subtitle="Contrats en cours"
            icon={<EventOutlinedIcon />}
          />

          <KpiCard
            title="Échéance ≤ 30 jours"
            value={summary.expiring_30_days || 0}
            subtitle="Contrats à surveiller"
            icon={<WarningAmberOutlinedIcon />}
          />

          <KpiCard
            title="Documents"
            value={summary.documents ?? documents.length}
            subtitle="Documents enregistrés"
            icon={<FolderOutlinedIcon />}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, value) =>
              setTab(value)
            }
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: {
                xs: 1,
                sm: 2,
              },
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Tab
              label={`Contrats (${contracts.length})`}
            />
            <Tab
              label={`Documents employés (${documents.length})`}
            />
          </Tabs>

          <Box
            sx={{
              p: 1.5,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md:
                  tab === 0
                    ? "minmax(220px, 1fr) 190px 190px"
                    : "1fr",
              },
              gap: 1.25,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <TextField
              size="small"
              placeholder={
                tab === 0
                  ? "Rechercher un contrat ou un employé..."
                  : "Rechercher un document ou un employé..."
              }
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {tab === 0 && (
              <TextField
                select
                size="small"
                label="Type"
                value={typeFilter}
                onChange={(event) =>
                  setTypeFilter(
                    event.target.value
                  )
                }
              >
                <MenuItem value="all">
                  Tous les types
                </MenuItem>

                {formData.contract_types.map((item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {tab === 0 && (
              <TextField
                select
                size="small"
                label="Statut"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >
                <MenuItem value="all">
                  Tous les statuts
                </MenuItem>

                {formData.statuses.map((item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <Box
            sx={{
              width: "100%",
              height: {
                xs: 520,
                md: 570,
              },
              overflow: "auto",
            }}
          >
            <DataGrid
              rows={
                tab === 0
                  ? contracts
                  : documents
              }
              columns={
                tab === 0
                  ? contractColumns
                  : documentColumns
              }
              loading={loading}
              disableRowSelectionOnClick
              rowHeight={58}
              pageSizeOptions={[
                5,
                10,
                25,
                50,
              ]}
              initialState={{
                pagination: {
                  paginationModel: {
                    page: 0,
                    pageSize: 10,
                  },
                },
              }}
              sx={{
                border: 0,
                minWidth:
                  tab === 0
                    ? 1050
                    : 1180,
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "action.hover",
                  fontWeight: 800,
                },
              }}
            />
          </Box>
        </Paper>


        {/* =================================================
            CREATE / EDIT CONTRACT
        ================================================= */}

        <Dialog
          open={contractDialogOpen}
          onClose={() =>
            !saving &&
            setContractDialogOpen(false)
          }
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editingContract
              ? `Modifier ${editingContract.contract_code}`
              : "Nouveau contrat"}
          </DialogTitle>

          <Divider />

          <DialogContent
            sx={{
              pt: "20px !important",
            }}
          >
            {!editingContract && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
              >
                Le numéro du contrat sera généré automatiquement lors de l'enregistrement. Exemple : <strong>CTR-00001</strong>.
              </Alert>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <TextField
                select
                required
                label="Employé"
                value={contractForm.employee_id}
                onChange={handleEmployeeChange}
              >
                <MenuItem value="">
                  Sélectionner
                </MenuItem>

                {formData.employees.map((employee) => (
                  <MenuItem
                    key={employee.id}
                    value={employee.id}
                  >
                    {employee.employee_code} — {employee.employee_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                required
                label="Type de contrat"
                value={contractForm.contract_type}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    contract_type:
                      event.target.value,
                  }))
                }
              >
                {formData.contract_types.map((item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                required
                type="date"
                label="Date de début"
                InputLabelProps={{ shrink: true }}
                value={contractForm.start_date}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    start_date:
                      event.target.value,
                  }))
                }
              />

              <TextField
                type="date"
                label="Date de fin"
                InputLabelProps={{ shrink: true }}
                value={contractForm.end_date}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    end_date:
                      event.target.value,
                  }))
                }
                helperText="Laisser vide pour un CDI sans échéance."
              />

              <TextField
                type="date"
                label="Fin période d'essai"
                InputLabelProps={{ shrink: true }}
                value={contractForm.probation_end_date}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    probation_end_date:
                      event.target.value,
                  }))
                }
              />

              <TextField
                type="date"
                label="Date de signature"
                InputLabelProps={{ shrink: true }}
                value={contractForm.signed_at}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    signed_at:
                      event.target.value,
                  }))
                }
              />

              <TextField
                type="number"
                label="Salaire contractuel"
                value={contractForm.salary}
                inputProps={{
                  min: 0,
                  step: 0.01,
                }}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    salary:
                      event.target.value,
                  }))
                }
              />

              <TextField
                select
                label="Devise"
                value={contractForm.currency}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    currency:
                      event.target.value,
                  }))
                }
              >
                {formData.currencies.map((item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Statut"
                value={contractForm.status}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    status:
                      event.target.value,
                  }))
                }
              >
                {formData.statuses.map((item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                multiline
                minRows={3}
                label="Notes / observations"
                value={contractForm.notes}
                onChange={(event) =>
                  setContractForm((current) => ({
                    ...current,
                    notes:
                      event.target.value,
                  }))
                }
                sx={{
                  gridColumn: {
                    xs: "auto",
                    sm: "1 / -1",
                  },
                }}
              />
            </Box>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() =>
                setContractDialogOpen(false)
              }
              disabled={saving}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={handleSaveContract}
              disabled={saving}
            >
              {editingContract
                ? "Enregistrer les modifications"
                : "Créer le contrat"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            RENEW DIALOG
        ================================================= */}

        <Dialog
          open={renewDialogOpen}
          onClose={() =>
            !saving &&
            setRenewDialogOpen(false)
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Renouveler le contrat
          </DialogTitle>

          <Divider />

          <DialogContent
            sx={{
              pt: "20px !important",
            }}
          >
            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              L'ancien contrat restera dans l'historique avec le statut <strong>Renouvelé</strong>. Un nouveau numéro CTR sera généré.
            </Alert>

            <Stack spacing={2}>
              <TextField
                type="date"
                required
                label="Début du nouveau contrat"
                InputLabelProps={{ shrink: true }}
                value={renewForm.start_date}
                onChange={(event) =>
                  setRenewForm((current) => ({
                    ...current,
                    start_date:
                      event.target.value,
                  }))
                }
              />

              <TextField
                type="date"
                label="Nouvelle date de fin"
                InputLabelProps={{ shrink: true }}
                value={renewForm.end_date}
                onChange={(event) =>
                  setRenewForm((current) => ({
                    ...current,
                    end_date:
                      event.target.value,
                  }))
                }
              />

              <TextField
                type="date"
                label="Fin période d'essai"
                InputLabelProps={{ shrink: true }}
                value={renewForm.probation_end_date}
                onChange={(event) =>
                  setRenewForm((current) => ({
                    ...current,
                    probation_end_date:
                      event.target.value,
                  }))
                }
              />

              <TextField
                type="date"
                label="Date de signature"
                InputLabelProps={{ shrink: true }}
                value={renewForm.signed_at}
                onChange={(event) =>
                  setRenewForm((current) => ({
                    ...current,
                    signed_at:
                      event.target.value,
                  }))
                }
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  type="number"
                  label="Nouveau salaire"
                  value={renewForm.salary}
                  inputProps={{ min: 0 }}
                  onChange={(event) =>
                    setRenewForm((current) => ({
                      ...current,
                      salary:
                        event.target.value,
                    }))
                  }
                />

                <TextField
                  select
                  label="Devise"
                  value={renewForm.currency}
                  onChange={(event) =>
                    setRenewForm((current) => ({
                      ...current,
                      currency:
                        event.target.value,
                    }))
                  }
                >
                  {formData.currencies.map((item) => (
                    <MenuItem
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <TextField
                multiline
                minRows={3}
                label="Notes du renouvellement"
                value={renewForm.notes}
                onChange={(event) =>
                  setRenewForm((current) => ({
                    ...current,
                    notes:
                      event.target.value,
                  }))
                }
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() =>
                setRenewDialogOpen(false)
              }
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={handleRenew}
              disabled={saving}
            >
              Renouveler
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            TERMINATION DIALOG
        ================================================= */}

        <Dialog
          open={terminationDialogOpen}
          onClose={() =>
            !saving &&
            setTerminationDialogOpen(false)
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Résilier le contrat
          </DialogTitle>

          <Divider />

          <DialogContent
            sx={{
              pt: "20px !important",
            }}
          >
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
            >
              Le contrat ne sera pas supprimé. Il restera conservé dans l'historique avec le statut Résilié.
            </Alert>

            <Stack spacing={2}>
              <TextField
                type="date"
                required
                label="Date de résiliation"
                InputLabelProps={{ shrink: true }}
                value={terminationForm.termination_date}
                onChange={(event) =>
                  setTerminationForm((current) => ({
                    ...current,
                    termination_date:
                      event.target.value,
                  }))
                }
              />

              <TextField
                required
                multiline
                minRows={4}
                label="Motif de la résiliation"
                value={terminationForm.reason}
                onChange={(event) =>
                  setTerminationForm((current) => ({
                    ...current,
                    reason:
                      event.target.value,
                  }))
                }
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() =>
                setTerminationDialogOpen(false)
              }
            >
              Annuler
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={handleTerminate}
              disabled={saving}
            >
              Confirmer la résiliation
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            DOCUMENT UPLOAD DIALOG
        ================================================= */}

        <Dialog
          open={documentDialogOpen}
          onClose={() =>
            !saving &&
            setDocumentDialogOpen(false)
          }
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            Ajouter un document employé
          </DialogTitle>

          <Divider />

          <DialogContent
            sx={{
              pt: "20px !important",
            }}
          >
            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              Choisissez directement le fichier à enregistrer. Formats acceptés : PDF, Word, JPG et PNG. Taille maximale : {formData.upload?.max_file_size_mb || 10} Mo.
            </Alert>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <TextField
                select
                required
                label="Employé"
                value={documentForm.employee_id}
                onChange={(event) =>
                  setDocumentForm((current) => ({
                    ...current,
                    employee_id:
                      event.target.value,
                    contract_id: "",
                  }))
                }
              >
                <MenuItem value="">
                  Sélectionner
                </MenuItem>

                {formData.employees.map((employee) => (
                  <MenuItem
                    key={employee.id}
                    value={employee.id}
                  >
                    {employee.employee_code} — {employee.employee_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Contrat associé"
                value={documentForm.contract_id}
                onChange={(event) =>
                  setDocumentForm((current) => ({
                    ...current,
                    contract_id:
                      event.target.value,
                  }))
                }
              >
                <MenuItem value="">
                  Aucun contrat
                </MenuItem>

                {documentContracts.map((contract) => (
                  <MenuItem
                    key={contract.id}
                    value={contract.id}
                  >
                    {contract.contract_code} — {CONTRACT_TYPE_LABELS[contract.contract_type] || contract.contract_type}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                required
                label="Type de document"
                value={documentForm.document_type}
                onChange={(event) =>
                  setDocumentForm((current) => ({
                    ...current,
                    document_type:
                      event.target.value,
                  }))
                }
              >
                {formData.document_types.map((item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                required
                label="Titre du document"
                value={documentForm.title}
                onChange={(event) =>
                  setDocumentForm((current) => ({
                    ...current,
                    title:
                      event.target.value,
                  }))
                }
              />

              <TextField
                type="date"
                label="Date d'émission"
                InputLabelProps={{ shrink: true }}
                value={documentForm.issue_date}
                onChange={(event) =>
                  setDocumentForm((current) => ({
                    ...current,
                    issue_date:
                      event.target.value,
                  }))
                }
              />

              <TextField
                type="date"
                label="Date d'expiration"
                InputLabelProps={{ shrink: true }}
                value={documentForm.expiry_date}
                onChange={(event) =>
                  setDocumentForm((current) => ({
                    ...current,
                    expiry_date:
                      event.target.value,
                  }))
                }
              />

              <Paper
                variant="outlined"
                sx={{
                  gridColumn: {
                    xs: "auto",
                    sm: "1 / -1",
                  },
                  p: 2,
                  borderStyle: "dashed",
                  borderWidth: 1.5,
                  bgcolor: "action.hover",
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileChange}
                />

                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  spacing={2}
                  alignItems={{
                    xs: "stretch",
                    sm: "center",
                  }}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      fontWeight={800}
                      sx={{ mb: 0.25 }}
                    >
                      Fichier à joindre
                    </Typography>

                    {selectedFile ? (
                      <>
                        <Typography
                          variant="body2"
                          noWrap
                        >
                          {selectedFile.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {formatBytes(selectedFile.size)}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Aucun fichier sélectionné.
                      </Typography>
                    )}
                  </Box>

                  <Button
                    variant="outlined"
                    startIcon={<DescriptionOutlinedIcon />}
                    onClick={() =>
                      fileInputRef.current?.click()
                    }
                    disabled={saving}
                  >
                    Choisir un fichier
                  </Button>
                </Stack>

                {saving && (
                  <Box sx={{ mt: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Téléversement
                      </Typography>
                      <Typography variant="caption">
                        {uploadProgress}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={uploadProgress}
                    />
                  </Box>
                )}
              </Paper>

              <TextField
                multiline
                minRows={3}
                label="Notes / observations"
                value={documentForm.notes}
                onChange={(event) =>
                  setDocumentForm((current) => ({
                    ...current,
                    notes:
                      event.target.value,
                  }))
                }
                sx={{
                  gridColumn: {
                    xs: "auto",
                    sm: "1 / -1",
                  },
                }}
              />
            </Box>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() =>
                setDocumentDialogOpen(false)
              }
              disabled={saving}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={handleUploadDocument}
              disabled={saving}
            >
              Téléverser le document
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            DELETE DOCUMENT CONFIRMATION
        ================================================= */}

        <Dialog
          open={Boolean(deleteDocumentTarget)}
          onClose={() =>
            !saving &&
            setDeleteDocumentTarget(null)
          }
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>
            Supprimer le document
          </DialogTitle>

          <DialogContent>
            <Typography>
              Voulez-vous supprimer définitivement <strong>{deleteDocumentTarget?.title || deleteDocumentTarget?.file_name || "ce document"}</strong> ?
            </Typography>

            <Alert
              severity="warning"
              sx={{ mt: 2 }}
            >
              Le fichier physique sera également supprimé du stockage local.
            </Alert>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() =>
                setDeleteDocumentTarget(null)
              }
              disabled={saving}
            >
              Annuler
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteDocument}
              disabled={saving}
            >
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>


        {/* =================================================
            SNACKBAR
        ================================================= */}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          onClose={() =>
            setSnackbar((current) => ({
              ...current,
              open: false,
            }))
          }
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() =>
              setSnackbar((current) => ({
                ...current,
                open: false,
              }))
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}
